import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box, DataGrid, IdentityCell } from '../../ui/core';
import { CarIcon, DriveIcon, EventIcon, MailIcon, PersonIcon, PhoneIcon } from '../../ui/icons';
import { fetchUsers } from '../../redux/Users/actions';
import { useQuery, useFormatters } from '../../hooks';
import { parseDateCreated } from '../../helpers/utils/parser';

// Same fields as before — headerIcon + the Name column's IdentityCell (avatar +
// name) are purely aesthetic, matching the clean table look.
const columns = [
  { field: 'id', hide: true },
  { field: 'createdAt', headerName: 'Created Date', headerIcon: <EventIcon />, flex: 1, minWidth: 160 },
  {
    field: 'name',
    headerName: 'Name',
    headerIcon: <PersonIcon />,
    flex: 1,
    minWidth: 200,
    renderCell: ({ row }) => <IdentityCell name={row.name} colorKey={row.id} />,
  },
  { field: 'email', headerName: 'Email', headerIcon: <MailIcon />, flex: 1, minWidth: 160 },
  { field: 'phoneNumber', headerName: 'Phone Number', headerIcon: <PhoneIcon />, minWidth: 240 },
  { field: 'type', headerName: 'Type', headerIcon: <CarIcon />, minWidth: 240 },
  { field: 'driver_id', headerName: 'Driver ID', headerIcon: <DriveIcon />, minWidth: 300 },
];

// A fixed page size rather than one derived from window.innerHeight — the
// row-fetch size shouldn't be coupled to how much chrome sits above the grid.
// The grid itself fills whatever height its flex parent gives it.
const PAGE_SIZE = 25;

const UsersTable = () => {
  const history = useHistory();
  let { page } = useQuery();
  page = Math.max(+page || 1, 1);
  const f = useFormatters();

  const { data, count, loading } = useSelector((state) => state.users.all);

  const rows = data.map((d) => ({
    ...d,
    id: d.id,
    createdAt: parseDateCreated(d.id),
    name: `${d.firstName} ${d.lastName}`,
    phoneNumber: f.phone.toInternational(d.phoneNumber),
    type: d.driver_id ? (d.isCustomDriver ? 'On-call driver' : 'Going anyway driver') : '',
  }));
  const dispatch = useDispatch();

  useEffect(() => {
    const query = {
      offset: (page - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
    };
    fetchUsers(query)(dispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, dispatch]);

  const onRowClick = ({ row }) => {
    history.push(`/users/${row.id}`);
  };

  const onPageChange = (p) => {
    const params = new URLSearchParams();
    params.append('page', p + 1);
    history.push({ search: params.toString() });
  };

  return (
    // No horizontal padding here: the DataGrid owns its own responsive gutter so
    // rows run full-bleed to the card edges (hairlines edge-to-edge) and the
    // first column lines up with the card header — the clean full-bleed table look.
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          onRowClick={onRowClick}
          pageSize={PAGE_SIZE}
          loading={loading}
          paginationMode="server"
          rowCount={count}
          page={page - 1}
          onPageChange={onPageChange}
        />
      </Box>
    </Box>
  );
};

export default UsersTable;
